import { IDropdownOption, Text } from '@fluentui/react';
import { Button, buttonStylesGhost, buttonStylesPrimary, Dropdown, Panel, useTheme } from '@h2oai/ui-kit';
import React, { useCallback, useEffect, useState } from 'react';

import { GetCustomerManagedGroupsRequest } from '../../../../../admin-center/gen/custom_group_management/custom_group_management_pb';
import { AddMemberRequest } from '../../../../../admin-center/gen/user_management/user_management_pb';
import { useAdminCenterService } from '../../../../../admin-center/hooks';
import { useUser } from '../../../../../utils/hooks';
import { buildUserContext } from '../../../common/utils';
import { UserRoleOptionKey } from '../../../constants';
import {
  bulkImportStyle,
  sampleAreaStyle,
  sampleTextStyle,
  sidePanelBtnStyle,
  sidePanelCancelBtnStyle,
  uploadAreaStyle,
  uploadTitleAreaStyle,
  uploadTitleStyle,
} from '../UserManagement.styles';

interface IPPanelProps {
  isBulkImport: boolean;
  setIsBulkImport: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmitSuccess?: () => Promise<void>;
  setAlertNotification: (isSuccess: boolean, message: string) => void;
  setResponseDialog: (users: string[]) => void;
  envId: string | null;
}

interface MemberInfo {
  firstName: string;
  lastName: string;
  email: string;
}

export default function BulkImportSidePanel({
  isBulkImport,
  setIsBulkImport,
  onSubmitSuccess,
  setAlertNotification,
  setResponseDialog,
  envId,
}: IPPanelProps) {
  const service = useAdminCenterService();
  const theme = useTheme();
  const user = useUser();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [customGroups, setCustomGroups] = useState<string[]>([]);
  const [selectedRoleKeys, setSelectedRoleKeys] = useState<string[]>([UserRoleOptionKey.user]);
  const [isRequestLoading, setIsRequestLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchCustomGroups = async () => {
      try {
        const req: GetCustomerManagedGroupsRequest = {};
        const response = await service.getCustomerManagedGroupsRequest(req);
        setCustomGroups(response.userGroups || []);
      } catch (error) {
        console.error('Error fetching custom groups:', error);
      }
    };
    if (isBulkImport) {
      fetchCustomGroups();
    }
  }, [isBulkImport, service, user]);

  const groupOptions = [
    { key: UserRoleOptionKey.user, text: 'Users' },
    { key: UserRoleOptionKey.admin, text: 'Admins' },
    ...customGroups.map((group) => ({ key: group, text: group })),
  ];

  const onRoleChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, _index?: number) => {
    if (!option) return;

    setSelectedRoleKeys((prevKeys: string[]) => {
      if (option.selected) {
        return [...prevKeys, option.key as string];
      } else {
        return prevKeys.filter((k) => k !== option.key);
      }
    });
  };

  const dismissPanel = useCallback(() => {
    setTimeout(() => {
      setSelectedRoleKeys([UserRoleOptionKey.user]);
      setIsBulkImport(false);
    }, 500);
  }, [setIsBulkImport, setSelectedRoleKeys]);
  const isFormValid = useCallback(() => {
    return selectedRoleKeys !== null && selectedRoleKeys !== undefined && selectedRoleKeys.length > 0;
  }, [selectedRoleKeys]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      validateAndSetFile(event.target.files[0]);
    }
  };

  const validateAndSetFile = (file: File) => {
    if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
      setSelectedFile(file);
      setErrorMessage(null);
    } else {
      setSelectedFile(null);
      setErrorMessage('❌ Invalid file type. Please upload a CSV file.');
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);

    if (event.dataTransfer.files.length > 0) {
      validateAndSetFile(event.dataTransfer.files[0]);
    }
  };

  function clearForm() {
    setSelectedFile(null);
    setSelectedRoleKeys([UserRoleOptionKey.user]);
  }

  const handleSubmitClick = useCallback(async () => {
    if (!isFormValid() || !selectedFile) return;
    const userContext = await buildUserContext(user);

    try {
      const text = await selectedFile.text();
      const rows = text
        .split('\n')
        .map((row) => row.trim())
        .filter((row) => row);

      const validMembers: MemberInfo[] = [];
      const errors: string[] = [];

      rows.forEach((row, index) => {
        const columns = row.split(',').map((col) => col.trim());

        if (columns.length < 3) {
          errors.push(`Row ${index + 1}: Missing columns`);
          return;
        }

        const [email, firstName, lastName] = columns;

        // Validate names and email format
        if (!firstName || !lastName || !email) {
          errors.push(`Row ${index + 1}: Column data missing`);
        } else if (!/\S+@\S+\.\S+/.test(email)) {
          errors.push(`Row ${index + 1}: Invalid email format`);
        } else {
          validMembers.push({ firstName, lastName, email });
        }
      });

      if (errors.length > 0) {
        setErrorMessage(errors.join('\n'));
      } else {
        setErrorMessage(null);
      }

      if (validMembers.length === 0) {
        setErrorMessage('No valid entries found.');
        return;
      }

      if (!envId && selectedRoleKeys.some((key) => key === UserRoleOptionKey.admin || key === UserRoleOptionKey.user)) {
        setAlertNotification(false, '❌ Error: Environment ID not available. Please refresh the page.');
        return;
      }

      const groupNames = selectedRoleKeys.map((key) => {
        if (key === UserRoleOptionKey.admin) return `hadc_${envId}_admin`;
        if (key === UserRoleOptionKey.user) return `hadc_${envId}_user`;
        return key; // custom group names are used as-is
      });

      const request: AddMemberRequest = {
        members: validMembers,
        groupNames: groupNames,
        modifiedBy: user?.name ?? '',
        timestamp: Date.now() / 1000,
        userContext: userContext,
      };

      const response = await service.addMembers(request);

      if (response.statusUpdate) {
        const statusMessage = response.statusUpdate;
        const match = statusMessage.match(/Failed to create users?: (.+)/);
        const failedEmails = match && match[1] ? match[1].split(',').map((email) => email.trim()) : [];

        if (failedEmails.length > 0) {
          setResponseDialog(failedEmails);
        } else {
          setAlertNotification(true, 'All users were added successfully');
        }
      } else {
        setErrorMessage('Failed to update members.');
      }

      if (onSubmitSuccess) {
        await onSubmitSuccess();
      }
      clearForm();
    } catch (error) {
      setAlertNotification(false, `Network error: ${error}`);
    } finally {
      setIsRequestLoading(false);
    }
  }, [
    isFormValid,
    selectedFile,
    selectedRoleKeys,
    user,
    onSubmitSuccess,
    isRequestLoading,
    setResponseDialog,
    setAlertNotification,
  ]);

  const onRenderFooterContent = useCallback(
    () => (
      <>
        <div style={sidePanelBtnStyle}>
          <Button styles={buttonStylesGhost} text="Cancel" onClick={dismissPanel} style={sidePanelCancelBtnStyle} />
          <Button
            styles={buttonStylesPrimary}
            text={isRequestLoading ? '...' : 'Add Members'}
            onClick={() => {
              setIsRequestLoading(true);
              handleSubmitClick();
            }}
            disabled={!(isFormValid() && !!selectedFile) || isRequestLoading}
          />
        </div>
      </>
    ),
    [dismissPanel, handleSubmitClick, isFormValid, selectedFile]
  );

  return (
    <Panel
      isLightDismiss
      isOpen={isBulkImport}
      onDismiss={dismissPanel}
      closeButtonAriaLabel="Close"
      isFooterAtBottom={true}
      onRenderFooterContent={onRenderFooterContent}
      customWidth="500px"
      type={7}
    >
      <h2>
        <strong>Add Users/Admins</strong>
      </h2>
      <Dropdown
        label="Groups"
        placeholder="Select groups"
        options={groupOptions}
        multiSelect
        selectedKeys={selectedRoleKeys}
        onChange={onRoleChange}
        required={true}
      />
      <div style={uploadTitleAreaStyle}>
        <Text variant="mediumPlus" styles={uploadTitleStyle}>
          Upload CSV
        </Text>
      </div>
      <div
        style={uploadAreaStyle(dragOver, theme)}
        onClick={() => document.getElementById('file-upload')?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input type="file" id="file-upload" accept=".csv" onChange={handleFileChange} style={bulkImportStyle} />
        <Text variant="small">
          {selectedFile ? `✔ Selected File: ${selectedFile.name}` : 'Drag & Drop CSV here or Click to Browse'}
        </Text>
      </div>
      <div style={sampleAreaStyle(theme)}>
        <strong>Sample CSV Format:</strong>
        <pre style={sampleTextStyle}>
          {`john@example.com,John,Doe
jane@example.com,Jane,Dane`}
        </pre>
      </div>
      {errorMessage && (
        <Text variant="small" styles={{ root: { color: theme.palette?.red300, marginTop: 5 } }}>
          {errorMessage}
        </Text>
      )}
    </Panel>
  );
}
